import {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import type {
  InspectionTemplate,
  TemplateItem,
  TemplateSection,
} from 'inspection-types';
import {
  useAddItemFromTemplate,
  useAddSectionFromTemplate,
  useInspectionsTemplateLibraryEnabled,
  useRemoveItemFromTemplate,
  useRemoveSectionFromTemplate,
} from 'react-hooks';

import ChannelAdminContext from '../../../../pages/portal/admin/channel/ChannelAdminContext';
import { TEMPLATE_LIBRARY_PATH } from '../../navigation/useInspectionsNavigation';

import {
  cloneTemplateSection,
  deriveInspectionTemplateAfterSectionRemoved,
  mergeRemovedSectionSnapshotsIntoOrderedSections,
} from '../../helpers/inspectionTemplateCustomizeHelpers';

import { useInspectionTemplateCustomizeExpandedState } from './useInspectionTemplateCustomizeExpandedState';
import { useInspectionTemplateCustomizeLabels } from './useInspectionTemplateCustomizeLabels';
import { useInspectionTemplateCustomizeRouteFetch } from './useInspectionTemplateCustomizeRouteFetch';
import { useInspectionTemplateCustomizeSectionObserver } from './useInspectionTemplateCustomizeSectionObserver';
import { useInspectionTemplateReorder } from './useInspectionTemplateReorder';

export type RemovedItemSnapshot = {
  sectionId: string;
  item: TemplateItem;
};

export type InspectionTemplateCustomizePageViewModel = {
  listPageUrl: string;
  hasValidRouteContext: boolean;
  template: InspectionTemplate | undefined;
  /** Present when the API returns a parent blueprint alongside the draft `template`. */
  baselineTemplate: InspectionTemplate | undefined;
  loading: boolean;
  error: Error | undefined;
  called: boolean;
  sortedSections: TemplateSection[];
  expandedBySectionId: Record<string, boolean>;
  activeSectionId: string | null;
  mainColumnRefCallback: (el: HTMLDivElement | null) => void;
  toggleSection: (sectionId: string) => void;
  handleCancel: () => void;
  summary: string | null;
  lastUpdatedText: string;
  saveDisabledTitle: string;
  revertAllDisabledTitle: string;
  removeSectionDisabledTitle: string;
  removeItemDisabledTitle: string;
  /** Item ids currently undergoing a remove mutation (supports concurrent removes). */
  removingItemIds: ReadonlySet<string>;
  /** Item ids currently undergoing a restore (supports concurrent restores). */
  restoringItemIds: ReadonlySet<string>;
  /** Section ids currently undergoing a remove mutation (supports concurrent removes). */
  removingSectionIds: ReadonlySet<string>;
  /** Section ids currently undergoing a restore mutation (supports concurrent restores). */
  restoringSectionIds: ReadonlySet<string>;
  /** Matches {@link useInspectionsTemplateLibraryEnabled} — gates restore on same entry as this page. */
  inspectionsTemplateLibraryEnabled: boolean;
  removeTemplateItem: (itemId: string) => Promise<void>;
  removeTemplateSection: (sectionId: string) => Promise<void>;
  restoreTemplateItem: (itemId: string) => Promise<void>;
  restoreTemplateSection: (sectionId: string) => Promise<void>;
  /**
   * Item IDs rendered with removed (strikethrough) presentation — includes
   * per-item overlays and every item under a soft-removed section.
   */
  removedOverlayItemIds: ReadonlySet<string>;
  /** Section IDs shown as removed (server section gone; client snapshot only). */
  removedSectionPresentationIds: ReadonlySet<string>;
  /** Whether the reorder controls and DnD interactions should be enabled. */
  reorderEnabled: boolean;
  /** True while one or more reorder mutations are still settling. */
  isReorderPending: boolean;
  /** Reorder sections by drag handle drop (active over target). */
  onReorderSections: (activeId: string, overId: string) => Promise<void>;
  /** Reorder items inside a single section by drag handle drop. */
  onReorderItemsInSection: (
    sectionId: string,
    activeId: string,
    overId: string
  ) => Promise<void>;
};

function findSectionAndItem(
  tmpl: InspectionTemplate,
  itemId: string
): { sectionId: string; item: TemplateItem } | undefined {
  for (const sec of tmpl.sections) {
    const item = sec.items.find(i => i.id === itemId);

    if (item) {
      return { sectionId: sec.id, item };
    }
  }

  return undefined;
}

function mergeSectionsWithRemovedOverlays(
  sections: TemplateSection[],
  removedSnapshots: Record<string, RemovedItemSnapshot>
): TemplateSection[] {
  return sections.map(section => {
    const serverIds = new Set(section.items.map(i => i.id));
    const overlayItems = Object.values(removedSnapshots)
      .filter(s => s.sectionId === section.id && !serverIds.has(s.item.id))
      .map(s => s.item);

    if (overlayItems.length === 0) {
      return {
        ...section,
        items: [...section.items].sort((a, b) => a.order - b.order),
      };
    }

    const items = [...section.items, ...overlayItems].sort(
      (a, b) => a.order - b.order
    );

    return { ...section, items };
  });
}

function sortSectionItems(section: TemplateSection): TemplateSection {
  return {
    ...section,
    items: [...section.items].sort((a, b) => a.order - b.order),
  };
}

function sortTemplateSections(sections: TemplateSection[]): TemplateSection[] {
  return [...sections].sort((a, b) => a.order - b.order).map(sortSectionItems);
}

function buildRemovedSectionPresentationIds(
  sections: TemplateSection[],
  removedSectionSnapshots: Record<string, TemplateSection>,
  serverSections: TemplateSection[]
): Set<string> {
  const serverSectionIds = new Set(serverSections.map(section => section.id));
  const ids = new Set<string>(
    Object.keys(removedSectionSnapshots).filter(id => !serverSectionIds.has(id))
  );

  for (const section of sections) {
    if (section.removed === true) {
      ids.add(section.id);
    }
  }

  return ids;
}

function buildRemovedOverlayItemIds(
  sections: TemplateSection[],
  removedSnapshots: Record<string, RemovedItemSnapshot>,
  removedSectionSnapshots: Record<string, TemplateSection>,
  serverSections: TemplateSection[]
): Set<string> {
  const serverItemIds = new Set(
    serverSections.flatMap(section => section.items.map(item => item.id))
  );
  const serverSectionIds = new Set(serverSections.map(section => section.id));
  const ids = new Set<string>(
    Object.values(removedSnapshots)
      .filter(snapshot => !serverItemIds.has(snapshot.item.id))
      .map(snapshot => snapshot.item.id)
  );

  for (const section of sections) {
    if (section.removed === true) {
      for (const item of section.items) {
        ids.add(item.id);
      }

      continue;
    }

    for (const item of section.items) {
      if (item.removed === true) {
        ids.add(item.id);
      }
    }
  }

  for (const section of Object.values(removedSectionSnapshots).filter(
    snapshot => !serverSectionIds.has(snapshot.id)
  )) {
    for (const item of section.items) {
      ids.add(item.id);
    }
  }

  return ids;
}

function buildRestoreItemPayload(item: TemplateItem): TemplateItem {
  const { removed: _removed, ...rest } = item;

  return rest;
}

function buildRestoreSectionPayload(section: TemplateSection): TemplateSection {
  const { removed: _removed, ...rest } = cloneTemplateSection(section);

  return {
    ...rest,
    items: rest.items.map(buildRestoreItemPayload),
  };
}

export function useInspectionTemplateCustomizePage(
  channelBasePath: string
): InspectionTemplateCustomizePageViewModel {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { channel } = useContext(ChannelAdminContext);
  const { templateId } = useParams<{ templateId: string }>();
  const mainColumnRef = useRef<HTMLDivElement | null>(null);

  const { removeItemFromTemplate: runRemoveItemMutation } =
    useRemoveItemFromTemplate();
  const { addItemFromTemplate: runAddItemMutation } = useAddItemFromTemplate();
  const { addSectionFromTemplate: runAddSectionMutation } =
    useAddSectionFromTemplate();
  const { removeSectionFromTemplate: runRemoveSectionMutation } =
    useRemoveSectionFromTemplate();
  const inspectionsTemplateLibraryEnabled =
    useInspectionsTemplateLibraryEnabled();

  const {
    template,
    baselineTemplate,
    loading,
    error,
    called,
    fetchCurrentInspectionTemplate,
  } = useInspectionTemplateCustomizeRouteFetch(channel?._id, templateId);

  const templateRef = useRef(template);

  templateRef.current = template;

  const [expandedBySectionId, setExpandedBySectionId] = useState<
    Record<string, boolean>
  >({});
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [removingItemIds, setRemovingItemIds] = useState(
    () => new Set<string>()
  );
  const [restoringItemIds, setRestoringItemIds] = useState(
    () => new Set<string>()
  );
  const [removingSectionIds, setRemovingSectionIds] = useState(
    () => new Set<string>()
  );
  const [restoringSectionIds, setRestoringSectionIds] = useState(
    () => new Set<string>()
  );
  const [removedSnapshots, setRemovedSnapshots] = useState<
    Record<string, RemovedItemSnapshot>
  >({});
  const [removedSectionSnapshots, setRemovedSectionSnapshots] = useState<
    Record<string, TemplateSection>
  >({});
  const [optimisticTemplate, setOptimisticTemplate] =
    useState<InspectionTemplate | null>(null);
  const [pendingReorderCount, setPendingReorderCount] = useState(0);

  const removingItemIdsRef = useRef(removingItemIds);

  removingItemIdsRef.current = removingItemIds;
  const restoringItemIdsRef = useRef(restoringItemIds);

  restoringItemIdsRef.current = restoringItemIds;
  const removingSectionIdsRef = useRef(removingSectionIds);

  removingSectionIdsRef.current = removingSectionIds;
  const restoringSectionIdsRef = useRef(restoringSectionIds);

  restoringSectionIdsRef.current = restoringSectionIds;

  /** Always latest overlays; restores read here to avoid stale `removedSnapshots` in callbacks. */
  const removedSnapshotsRef = useRef(removedSnapshots);

  removedSnapshotsRef.current = removedSnapshots;

  const removedSectionSnapshotsRef = useRef(removedSectionSnapshots);

  removedSectionSnapshotsRef.current = removedSectionSnapshots;

  const listPageUrl = `${channelBasePath}/${TEMPLATE_LIBRARY_PATH}`;

  /**
   * Strikethrough / undo for removed sections and questions applies only for the
   * current editor visit. Each navigation gets a new `location.key` (e.g. list →
   * edit again), so we drop client overlays and show only `currentInspectionTemplate`.
   */
  useLayoutEffect(() => {
    if (!channel?._id || !templateId) {
      return;
    }

    setRemovedSectionSnapshots({});
    setRemovedSnapshots({});
  }, [location.key, channel?._id, templateId]);

  const effectiveTemplate = optimisticTemplate ?? template;

  /**
   * Tracks how many reorder mutations are still in flight. We don't piggy-back on
   * `template.version`: the server owns that counter and may not return the value
   * we expect (batched bumps, concurrent edits, etc.), which would strand the
   * optimistic overlay.
   */
  const pendingReorderCountRef = useRef(0);
  const isReorderPending = pendingReorderCount > 0;

  const sortedSections = useMemo((): TemplateSection[] => {
    if (!effectiveTemplate) {
      return [];
    }

    const ordered = sortTemplateSections(effectiveTemplate.sections);
    // Section snapshots are in-session only; cleared on new navigation (see location.key effect).
    const withSectionOverlays =
      mergeRemovedSectionSnapshotsIntoOrderedSections(
        ordered,
        removedSectionSnapshots
      );

    return mergeSectionsWithRemovedOverlays(
      withSectionOverlays,
      removedSnapshots
    );
  }, [
    effectiveTemplate,
    removedSnapshots,
    removedSectionSnapshots,
  ]);

  const serverSortedSections = useMemo((): TemplateSection[] => {
    if (!effectiveTemplate) {
      return [];
    }

    return sortTemplateSections(effectiveTemplate.sections);
  }, [effectiveTemplate]);

  const removedSectionPresentationIds = useMemo(
    () =>
      buildRemovedSectionPresentationIds(
        sortedSections,
        removedSectionSnapshots,
        serverSortedSections
      ),
    [removedSectionSnapshots, serverSortedSections, sortedSections]
  );

  const removedOverlayItemIds = useMemo(
    () =>
      buildRemovedOverlayItemIds(
        sortedSections,
        removedSnapshots,
        removedSectionSnapshots,
        serverSortedSections
      ),
    [removedSnapshots, removedSectionSnapshots, serverSortedSections, sortedSections]
  );

  useInspectionTemplateCustomizeExpandedState({
    template,
    sortedSections,
    setExpandedBySectionId,
    setActiveSectionId,
  });

  useInspectionTemplateCustomizeSectionObserver({
    template,
    sortedSections,
    mainColumnRef,
    setActiveSectionId,
  });

  const handleCancel = useCallback(() => {
    history.push(listPageUrl);
  }, [history, listPageUrl]);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedBySectionId(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  const mainColumnRefCallback = useCallback((el: HTMLDivElement | null) => {
    mainColumnRef.current = el;
  }, []);

  const labels = useInspectionTemplateCustomizeLabels(template, t);

  const hasValidRouteContext = Boolean(channel?._id && templateId);

  const removeTemplateItem = useCallback(
    async (itemId: string) => {
      if (!channel?._id || !templateId) {
        return;
      }

      if (
        pendingReorderCountRef.current > 0 ||
        removingSectionIdsRef.current.size > 0 ||
        restoringSectionIdsRef.current.size > 0
      ) {
        return;
      }

      const templateAtStart = templateRef.current;

      if (!templateAtStart) {
        return;
      }

      const located = findSectionAndItem(templateAtStart, itemId);

      if (!located) {
        return;
      }

      setRemovingItemIds(prev => new Set(prev).add(itemId));

      let removeSucceeded = false;

      try {
        await runRemoveItemMutation({
          channelId: channel._id,
          templateId,
          itemId,
        });

        setRemovedSnapshots(prev => ({
          ...prev,
          [itemId]: {
            sectionId: located.sectionId,
            item: located.item,
          },
        }));

        await fetchCurrentInspectionTemplate({
          id: templateId,
          channelId: channel._id,
          includeHidden: true,
        });

        removeSucceeded = true;
      } catch {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.removeItemErrorToast')
        );
      } finally {
        setRemovingItemIds(prev => {
          const next = new Set(prev);

          next.delete(itemId);

          return next;
        });
      }

      if (removeSucceeded) {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.removeItemSuccessToast')
        );
      }
    },
    [
      channel?._id,
      fetchCurrentInspectionTemplate,
      runRemoveItemMutation,
      t,
      templateId,
    ]
  );

  const restoreTemplateItem = useCallback(
    async (itemId: string) => {
      if (!channel?._id || !templateId) {
        return;
      }

      if (
        pendingReorderCountRef.current > 0 ||
        restoringSectionIdsRef.current.size > 0 ||
        removingSectionIdsRef.current.size > 0
      ) {
        return;
      }

      let sectionIdForRestore: string;
      let itemForRestore: TemplateItem;
      const templateAtStart = templateRef.current;
      const templateHit = templateAtStart
        ? findSectionAndItem(templateAtStart, itemId)
        : undefined;

      if (templateHit) {
        sectionIdForRestore = templateHit.sectionId;
        itemForRestore = buildRestoreItemPayload(templateHit.item);
      } else {
        const snapshot = removedSnapshotsRef.current[itemId];

        if (!snapshot) {
          await window.Toast.show(
            t('web.admin.inspections.templateCustomize.restoreItemErrorToast')
          );

          return;
        }

        sectionIdForRestore = snapshot.sectionId;
        itemForRestore = buildRestoreItemPayload(snapshot.item);
      }

      setRestoringItemIds(prev => new Set(prev).add(itemId));

      let restoreSucceeded = false;

      try {
        await runAddItemMutation({
          channelId: channel._id,
          templateId,
          sectionId: sectionIdForRestore,
          item: itemForRestore,
        });

        await fetchCurrentInspectionTemplate({
          id: templateId,
          channelId: channel._id,
          includeHidden: true,
        });

        setRemovedSnapshots(prev => {
          const next = { ...prev };

          delete next[itemId];

          return next;
        });

        restoreSucceeded = true;
      } catch {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.restoreItemErrorToast')
        );
      } finally {
        setRestoringItemIds(prev => {
          const next = new Set(prev);

          next.delete(itemId);

          return next;
        });
      }

      if (restoreSucceeded) {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.restoreItemSuccessToast')
        );
      }
    },
    [
      channel?._id,
      fetchCurrentInspectionTemplate,
      runAddItemMutation,
      t,
      templateId,
    ]
  );

  const restoreTemplateSection = useCallback(
    async (sectionId: string) => {
      if (!channel?._id || !templateId || !inspectionsTemplateLibraryEnabled) {
        return;
      }

      if (restoringSectionIdsRef.current.has(sectionId)) {
        return;
      }

      if (
        pendingReorderCountRef.current > 0 ||
        removingSectionIdsRef.current.size > 0 ||
        restoringItemIdsRef.current.size > 0 ||
        removingItemIdsRef.current.size > 0
      ) {
        return;
      }

      let sectionPayloadToAdd: TemplateSection;
      const templateAtStart = templateRef.current;
      const templateSection = templateAtStart?.sections.find(
        s => s.id === sectionId
      );

      if (templateSection) {
        sectionPayloadToAdd = buildRestoreSectionPayload(templateSection);
      } else {
        const snapshot =
          removedSectionSnapshotsRef.current[sectionId];

        if (!snapshot) {
          await window.Toast.show(
            t('web.admin.inspections.templateCustomize.restoreSectionErrorToast')
          );

          return;
        }

        sectionPayloadToAdd = buildRestoreSectionPayload(snapshot);
      }

      setRestoringSectionIds(prev => new Set(prev).add(sectionId));

      try {
        await runAddSectionMutation({
          channelId: channel._id,
          templateId,
          section: sectionPayloadToAdd,
        });

        await fetchCurrentInspectionTemplate({
          id: templateId,
          channelId: channel._id,
          includeHidden: true,
        });

        setRemovedSectionSnapshots(prev => {
          const next = { ...prev };

          delete next[sectionId];

          return next;
        });

        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.restoreSectionSuccessToast')
        );
      } catch {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.restoreSectionErrorToast')
        );
      } finally {
        setRestoringSectionIds(prev => {
          const next = new Set(prev);

          next.delete(sectionId);

          return next;
        });
      }
    },
    [
      channel?._id,
      fetchCurrentInspectionTemplate,
      inspectionsTemplateLibraryEnabled,
      runAddSectionMutation,
      t,
      templateId,
    ]
  );

  const removeTemplateSection = useCallback(
    async (sectionId: string) => {
      if (!channel?._id || !templateId) {
        return;
      }

      if (removingSectionIdsRef.current.has(sectionId)) {
        return;
      }

      if (
        pendingReorderCountRef.current > 0 ||
        restoringSectionIdsRef.current.size > 0 ||
        restoringItemIdsRef.current.size > 0 ||
        removingItemIdsRef.current.size > 0
      ) {
        return;
      }

      const templateAtStart = templateRef.current;

      if (!templateAtStart) {
        return;
      }

      const baseSection = templateAtStart.sections.find(s => s.id === sectionId);

      if (!baseSection) {
        return;
      }

      let mergedItems: TemplateItem[] = [];

      const overlayItemsForSection = Object.values(
        removedSnapshotsRef.current
      )
        .filter(s => s.sectionId === sectionId)
        .map(s => s.item);

      const itemById = new Map<string, TemplateItem>();

      for (const item of baseSection.items) {
        itemById.set(item.id, item);
      }

      for (const item of overlayItemsForSection) {
        itemById.set(item.id, item);
      }

      mergedItems = [...itemById.values()].sort(
        (a, b) => a.order - b.order
      );

      setRemovingSectionIds(prev => new Set(prev).add(sectionId));

      let removeSucceeded = false;

      try {
        const { newVersion } = await runRemoveSectionMutation({
          channelId: channel._id,
          templateId,
          sectionId,
        });

        const nextTemplate = deriveInspectionTemplateAfterSectionRemoved(
          templateAtStart,
          sectionId,
          newVersion
        );

        setRemovedSectionSnapshots(prev => ({
          ...prev,
          [sectionId]: cloneTemplateSection({
            ...baseSection,
            items: mergedItems,
          }),
        }));

        setRemovedSnapshots(prev => {
          const next = { ...prev };

          for (const [itemId, snap] of Object.entries(next)) {
            if (snap.sectionId === sectionId) {
              delete next[itemId];
            }
          }

          return next;
        });

        setActiveSectionId(prev => {
          if (prev !== sectionId) {
            return prev;
          }

          const ordered = [...nextTemplate.sections].sort(
            (a, b) => a.order - b.order
          );

          return ordered[0]?.id ?? null;
        });

        await fetchCurrentInspectionTemplate({
          id: templateId,
          channelId: channel._id,
          includeHidden: true,
        });

        removeSucceeded = true;
      } catch {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.removeSectionErrorToast')
        );
      } finally {
        setRemovingSectionIds(prev => {
          const next = new Set(prev);

          next.delete(sectionId);

          return next;
        });
      }

      if (removeSucceeded) {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.removeSectionSuccessToast')
        );
      }
    },
    [
      channel?._id,
      fetchCurrentInspectionTemplate,
      runRemoveSectionMutation,
      t,
      templateId,
    ]
  );

  const refetchTemplate = useCallback(async () => {
    if (!channel?._id || !templateId) {
      return;
    }

    await fetchCurrentInspectionTemplate({
      id: templateId,
      channelId: channel._id,
      includeHidden: true,
    });
  }, [channel?._id, fetchCurrentInspectionTemplate, templateId]);

  const applyOptimisticReorder = useCallback((next: InspectionTemplate) => {
    pendingReorderCountRef.current += 1;
    setPendingReorderCount(pendingReorderCountRef.current);
    setOptimisticTemplate(next);
  }, []);

  const settleOptimisticReorder = useCallback(() => {
    pendingReorderCountRef.current = Math.max(
      0,
      pendingReorderCountRef.current - 1
    );
    setPendingReorderCount(pendingReorderCountRef.current);

    if (pendingReorderCountRef.current === 0) {
      setOptimisticTemplate(null);
    }
  }, []);

  const rollbackOptimisticReorder = useCallback(() => {
    pendingReorderCountRef.current = Math.max(
      0,
      pendingReorderCountRef.current - 1
    );
    setPendingReorderCount(pendingReorderCountRef.current);

    if (pendingReorderCountRef.current === 0) {
      setOptimisticTemplate(null);
    }
  }, []);

  /**
   * Reorder is mutually exclusive with in-flight remove/restore mutations.
   * Reads refs so the live state is checked at drag time, not at hook render time.
   */
  const isReorderBlocked = useCallback(
    () =>
      removingItemIdsRef.current.size > 0 ||
      restoringItemIdsRef.current.size > 0 ||
      removingSectionIdsRef.current.size > 0 ||
      restoringSectionIdsRef.current.size > 0,
    []
  );

  const {
    reorderEnabled,
    reorderSection: onReorderSections,
    reorderItemWithinSection: onReorderItemsInSection,
  } = useInspectionTemplateReorder({
    channelId: channel?._id,
    templateId,
    template: effectiveTemplate,
    isReorderPending,
    isReorderBlocked,
    applyOptimistic: applyOptimisticReorder,
    settle: settleOptimisticReorder,
    rollback: rollbackOptimisticReorder,
    refetch: refetchTemplate,
  });

  return {
    listPageUrl,
    hasValidRouteContext,
    template,
    baselineTemplate,
    loading,
    error,
    called,
    sortedSections,
    expandedBySectionId,
    activeSectionId,
    mainColumnRefCallback,
    toggleSection,
    handleCancel,
    summary: labels.summary,
    lastUpdatedText: labels.lastUpdatedText,
    saveDisabledTitle: labels.saveDisabledTitle,
    revertAllDisabledTitle: labels.revertAllDisabledTitle,
    removeSectionDisabledTitle: labels.removeSectionDisabledTitle,
    removeItemDisabledTitle: labels.removeItemDisabledTitle,
    removingItemIds,
    restoringItemIds,
    removingSectionIds,
    restoringSectionIds,
    inspectionsTemplateLibraryEnabled,
    removeTemplateItem,
    removeTemplateSection,
    restoreTemplateItem,
    restoreTemplateSection,
    removedOverlayItemIds,
    removedSectionPresentationIds,
    reorderEnabled,
    isReorderPending,
    onReorderSections,
    onReorderItemsInSection,
  };
}
